import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { TripWaypointsList } from "./trips-waypoints-list";
import { useMap } from "@vis.gl/react-google-maps";
import { Loader2, Truck } from "lucide-react";
import { useTripsStore } from "@/screens/dashboard/trip-temp-order.store";
import { useActiveTrips, useTripWaypoints } from "@/api/trips";
import { useSelectedWaypointStore } from "@/screens/dashboard/selected-waypoint.store";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Waypoint } from "@/api.types";

export function TripsList() {
  const map = useMap();
  const setTemporaryWaypointOrder = useTripsStore(
    (state) => state.setTemporaryWaypointOrder
  );
  const setSelectedWaypoint = useSelectedWaypointStore(
    (state) => state.setSelectedWaypoint
  );

  // Fetch active trips with pagination
  const tripsQuery = useActiveTrips();

  const trips = tripsQuery.data?.data;

  // Create queries for waypoints of each trip
  const waypointQueries = useTripWaypoints(trips);

  // Handler for waypoint selection that uses the new store interface
  const handleWaypointSelect = (
    waypoint: Waypoint | null,
    tripContextId?: string
  ) => {
    if (waypoint) {
      // The tripId might come from the waypoint.trip.id or from the surrounding trip context
      const effectiveTripId = waypoint.trip?.id ?? tripContextId;

      if (effectiveTripId) {
        setSelectedWaypoint(effectiveTripId, waypoint.id);
      }
    } else {
      setSelectedWaypoint(null, null);
    }
  };

  const handleAccordionChange = (tripId: string) => {
    const trip = trips?.find((t) => t.id === tripId);
    if (!trip?.plannedRoute?.routes?.[0]?.polyline?.encodedPolyline || !map) {
      return;
    }

    try {
      const path = google.maps.geometry.encoding.decodePath(
        trip.plannedRoute.routes[0].polyline.encodedPolyline
      );

      const bounds = new google.maps.LatLngBounds();
      path.forEach((point) => bounds.extend(point));

      // Add some padding around the bounds
      map.fitBounds(bounds);
    } catch (error) {
      console.error("Failed to fit bounds for trip:", error);
    }
  };

  if (tripsQuery.isLoading || waypointQueries.some((q) => q.isLoading))
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );

  if (!trips || trips.length === 0)
    return (
      <div className="flex-1 flex items-center justify-center">
        No active trips
      </div>
    );

  return (
    <div className="h-full overflow-hidden flex flex-col">
      <div className="py-2 px-4 bg-white border-b">
        <h2 className="text-xl text-center font-bold">Trips</h2>
      </div>
      <Accordion
        type="single"
        collapsible
        defaultValue={trips?.[0]?.id}
        onValueChange={handleAccordionChange}
        className="overflow-y-auto flex-1"
      >
        {trips.map((trip, index) => {
          // Calculate completion percentage for the trip
          const waypoints = waypointQueries[index]?.data ?? [];
          const allTasks = waypoints.flatMap((waypoint) => waypoint.tasks);
          const completedTasksCount = allTasks.filter((task) =>
            ["COMPLETED", "FAILED", "CANCELLED"].includes(task.status)
          ).length;
          const completionPercentage =
            allTasks.length > 0
              ? (completedTasksCount / allTasks.length) * 100
              : 0;

          return (
            <AccordionItem
              key={trip.id}
              value={trip.id}
              className="bg-gray-100 border-b-2"
            >
              <AccordionTrigger className="hover:no-underline py-2 items-center justify-between relative overflow-hidden pr-4">
                <div className="flex flex-row justify-between items-center w-full relative z-10">
                  <span className="text-lg font-medium truncate flex-1 text-left px-4 flex items-center gap-2">
                    {trip.operator ? (
                      <Avatar className="h-7 w-7">
                        {trip.operator.user?.picture && (
                          <AvatarImage
                            src={trip.operator.user.picture}
                            alt={`${trip.operator.user.firstName} ${trip.operator.user.lastName}`}
                          />
                        )}
                        <AvatarFallback
                          className="text-xs text-white font-semibold bg-orange-500"
                          style={{ backgroundColor: trip.color }}
                        >
                          {trip.operator.user?.firstName?.[0]}
                          {trip.operator.user?.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <Avatar className="h-7 w-7">
                        <AvatarFallback
                          className="text-sm text-white font-semibold"
                          style={{ backgroundColor: trip.color ?? "#CBD5E0" }}
                        >
                          ?
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className="relative">
                      <Truck className="h-6 w-6" />
                      <div
                        className="h-[4px] rounded-sm"
                        style={{ backgroundColor: trip.color ?? "#CBD5E0" }}
                      />
                    </div>
                    #{trip.tripNumber ?? "N/A"}
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 mr-2 py-0.5 text-xs font-medium rounded-full ${
                        trip.status === "PLANNED"
                          ? "bg-yellow-100 text-yellow-800"
                          : trip.status === "ACTIVE"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                      }`}
                    >
                      {trip.status}
                    </span>
                  </div>
                </div>
              </AccordionTrigger>

              {/* Progress bar */}
              <div className="h-1.5 w-full bg-gray-200">
                <div
                  className="h-full bg-[#4CAF50]"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>

              <AccordionContent className="px-2">
                <TripWaypointsList
                  tripId={trip.id}
                  onTemporaryOrderChange={setTemporaryWaypointOrder}
                  onWaypointSelect={(waypoint) =>
                    handleWaypointSelect(waypoint, trip.id)
                  }
                />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
